var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from 'axios';
import { AuthenticationMethodType, AuthenticationReason, AuthenticationResult, AuthenticationStatus } from '@mc-alberta/types';
import { fidoAuthenticatorToMfaMethod, getAuthenticationContext } from './transforms';
import { getAuthenticator, getFidoUserName, getMfaMethodKpiMeta, createPublicKey, getPublicKey, isFidoDeviceBounded, isFidoDeviceRecognized, isFidoSupported, isSecurePaymentConfirmationSupported } from './utils';
import { Adapter } from '../Adapter';
import { AuthenticationStrategy } from '../../types/enums/AuthenticationStrategy';
import { DasAuthenticationStatus } from '../../types/enums/DasAuthenticationStatus';
import { KpiOperation } from '../../types/enums/KpiOperation';
import { Kpi } from '../../loggers/Kpi';
import { transformDasError } from '../../loggers/utils';
export class FidoAdapter extends Adapter {
    /** Whether or not the cardholder and/or device can be enrolled into FIDO. */
    get isEnrollable() {
        if (!this.authenticationMethod)
            return false;
        if (!this.isDeviceEligible)
            return false;
        if (isFidoDeviceRecognized(this.authenticationMethod)) {
            // device is recognized - cardholder can enroll as long as it's not already bounded to the card
            return !isFidoDeviceBounded(this.authenticationMethod);
        }
        return true; // device is not recognized
    }
    /** True if FIDO can be used for authentication or enrollment. */
    get isUsable() {
        const isAuthenticatable = this.isPaymentAuthentication && isFidoDeviceBounded(this.authenticationMethod);
        return this.isEnrollable || isAuthenticatable;
    }
    constructor(payload) {
        super(payload);
        this.strategy = AuthenticationStrategy.FIDO2;
        /** The device recognition JWT. */
        this.deviceRecognitionToken = null;
        /** Whether or not this device is eligible to use FIDO (enrollment or authentication). */
        this.isDeviceEligible = false;
        /**
         * Bootstraps the FIDO adapter.
         */
        this.bootstrap = (authenticationMethods) => __awaiter(this, void 0, void 0, function* () {
            this.applyAuthenticationMethods(authenticationMethods);
            this.isDeviceEligible = yield isFidoSupported();
        });
        /**
         * Initializes a new DAS session from the provider data in the given FIDO method.
         */
        this.initializeSession = () => __awaiter(this, void 0, void 0, function* () {
            if (this.isEnrollmentPending) {
                this.kpi.start(KpiOperation.INITIATE_FIDO_REGISTRATION);
            }
            else if (isFidoDeviceBounded(this.authenticationMethod)) {
                this.kpi.start(KpiOperation.INITIATE_AUTHENTICATION);
            }
            const { authenticationContext, authenticationMethod } = this;
            const { extAuthProviderMetaData, fidoUserName } = authenticationMethod.methodAttributes;
            this.dasAuthenticationSessionId = extAuthProviderMetaData.authSessionId;
            this.authorization = extAuthProviderMetaData.accessToken;
            if (isFidoDeviceRecognized(this.authenticationMethod)) {
                if (isFidoDeviceBounded(this.authenticationMethod) || this.isEnrollmentPending) {
                    // user has a recognized authenticator; use it for either
                    // authentication (if bound) or enrollment (if unbound)
                    yield this.authenticate(authenticationContext, authenticationMethod);
                    return this.getSession();
                }
            }
            if (this.isEnrollmentPending) {
                // device is not recognized but ID&V has been performed; enroll in FIDO
                yield this.fetchPublicKeyCredentialCreationOptions(fidoUserName || getFidoUserName());
                return this.getSession();
            }
            return this.destroySession();
        });
        /**
         * Provides the WebAuthn challenge to create/request the public key credential.
         */
        this.provideChallenge = () => __awaiter(this, void 0, void 0, function* () {
            this.authenticationStatus = AuthenticationStatus.PENDING;
            try {
                this.kpi.start(KpiOperation.PROVIDE_AUTHENTICATION_CHALLENGE);
                if (this.publicKeyCredentialRequestOptions) {
                    // authentication
                    this.publicKeyWithAssertion = yield getPublicKey(this.publicKeyCredentialRequestOptions);
                }
                else if (this.publicKeyCredentialCreationOptions) {
                    // enrollment
                    this.publicKeyWithAttestation = yield createPublicKey(this.publicKeyCredentialCreationOptions);
                }
                else {
                    return this.destroySession('No credential options available.');
                }
                return this.getSession();
            }
            catch (error) {
                this.kpi.queueError(error);
                // consider the user to have 'failed' the challenge (even if they clumsily hit 'cancel')
                this.authenticationStatus = AuthenticationStatus.CHALLENGE_FAILED;
                this.authenticationResult = AuthenticationResult.NOT_AUTHENTICATED;
            }
            return this.getSession();
        });
        /**
         * Validates the public key credentials.
         */
        this.validateSession = () => __awaiter(this, void 0, void 0, function* () {
            if (this.isEnrollmentPending) {
                this.kpi.start(KpiOperation.COMPLETE_FIDO_REGISTRATION);
            }
            else if (isFidoDeviceBounded(this.authenticationMethod)) {
                this.kpi.start(KpiOperation.COMPLETE_AUTHENTICATION);
            }
            const { authenticationContext, authenticationMethod } = this;
            const authenticator = !isFidoDeviceRecognized(this.authenticationMethod)
                ? yield this.enroll()
                : yield this.authenticate(authenticationContext, authenticationMethod);
            if (authenticator) {
                // authentication or enrollment was successful; update auth provider with the authenticator
                return this.refreshSession({
                    boundMfaMethod: fidoAuthenticatorToMfaMethod(authenticator),
                    extAuthProviderMetaData: {
                        authSessionId: this.dasAuthenticationSessionId,
                        accessToken: this.authorization
                    }
                }, {
                    authenticationContext: Object.assign(Object.assign({}, this.authenticationContext), { consumerDeviceData: yield this.getConsumerDeviceData(), authenticationReasons: [
                            // ensure the correct reason based on enrollment (binding) or authentication
                            this.isEnrollmentPending
                                ? AuthenticationReason.ENROL_FINANCIAL_INSTRUMENT
                                : AuthenticationReason.TRANSACTION_AUTHENTICATION
                        ] })
                });
            }
            // MACS authentication was not successful; return the most recent state
            return this.getSession();
        });
        /**
         * Enrolls a new FIDO user.
         * On its first call, this method will request the necessary biometric from the user.
         */
        this.enroll = () => __awaiter(this, void 0, void 0, function* () {
            // credentials have been collected - send them to DAS and return its result
            const authenticator = yield this.post('registrations/completions', {
                publicKeyCredentials: this.publicKeyWithAttestation
            });
            this.deviceRecognitionToken = authenticator === null || authenticator === void 0 ? void 0 : authenticator.deviceRecognitionToken;
            return authenticator;
        });
        /**
         * Creates a new public key credential based on configuration retrieved from DAS.
         */
        this.fetchPublicKeyCredentialCreationOptions = (fidoUserName) => __awaiter(this, void 0, void 0, function* () {
            const consumerDeviceData = yield this.getConsumerDeviceData();
            const data = yield this.post('registrations/initiations', {
                authenticatorType: AuthenticationMethodType.FIDO2,
                consumerDeviceData,
                fidoUserName
            });
            this.publicKeyCredentialCreationOptions = data === null || data === void 0 ? void 0 : data.publicKeyCredentialCreationOptions;
            this.authenticationStatus = AuthenticationStatus.PENDING_CHALLENGE;
        });
        /**
         * Authenticates a FIDO user.
         * On its first call, this method will request the necessary biometric from the user.
         */
        this.authenticate = (authenticationContext, authenticationMethod) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            const consumerDeviceData = yield this.getConsumerDeviceData();
            const results = yield this.post('authentications', {
                authenticationContext: getAuthenticationContext(authenticationContext),
                authenticationMethod: Object.assign(Object.assign({}, (_a = getAuthenticator(authenticationMethod)) === null || _a === void 0 ? void 0 : _a.authenticator), { methodAttributes: {
                        publicKeyCredential: this.publicKeyWithAssertion
                    } }),
                consumerDeviceData
            });
            switch (results === null || results === void 0 ? void 0 : results.authenticationStatus) {
                case DasAuthenticationStatus.INITIATED:
                    this.publicKeyCredentialRequestOptions = results
                        .methodAttributes
                        .publicKeyCredentialRequestOptions;
                    this.authenticationStatus = AuthenticationStatus.PENDING_CHALLENGE;
                    return;
                case DasAuthenticationStatus.COMPLETE:
                    return this.getValidatedAuthenticator(results);
                default:
                    throw new Error(results === null || results === void 0 ? void 0 : results.authenticationStatus);
            }
        });
        /**
         * Returns the validated authenticator instance from a successful authentication response.
         */
        this.getValidatedAuthenticator = ({ methodAttributes: { unboundAuthenticator, boundAuthenticator }, proofOfAuthentication }) => {
            delete this.publicKeyWithAssertion;
            const authenticator = unboundAuthenticator
                ? { authenticator: unboundAuthenticator }
                : boundAuthenticator;
            if (authenticator) {
                authenticator
                    .authenticator
                    .assertionData = { proofOfAuthentication };
            }
            return authenticator;
        };
        /**
         * Returns the device recognition JWT.
         * If not already fetched, this will make a call to DAS to get it.
         * If no recognition token exists on the device, this returns `undefined`.
         */
        this.getRecognitionToken = () => __awaiter(this, void 0, void 0, function* () {
            if (this.deviceRecognitionToken !== null) {
                return this.deviceRecognitionToken;
            }
            try {
                const isEligible = yield isFidoSupported();
                if (!isEligible)
                    return;
                const { data } = yield axios.get(`${this.dasHost}/devices/recognitions`, {
                    withCredentials: true,
                    params: {
                        client_id: this.dasClientId,
                        client_session_id: this.dasClientSessionId
                    },
                    headers: {
                        'correlation-id': this.dasClientSessionId
                    }
                });
                this.deviceRecognitionToken = data.deviceRecognitionToken;
                return data.deviceRecognitionToken;
            }
            catch (_) {
                delete this.deviceRecognitionToken;
            }
        });
        /**
         * Returns device attributes, including the recognition token (if exists).
         */
        this.getConsumerDeviceData = () => __awaiter(this, void 0, void 0, function* () {
            const deviceRecognitionToken = yield this.getRecognitionToken();
            const spcSupported = yield isSecurePaymentConfirmationSupported();
            return {
                userAgent: navigator.userAgent,
                screenWidth: screen.width,
                screenHeight: screen.height,
                spcSupported,
                deviceRecognitionToken
            };
        });
        /**
         * Makes a `POST` request to DAS. This will attach any necessary identifiers/authorization.
         */
        this.post = (path, params) => __awaiter(this, void 0, void 0, function* () {
            var _b, _c;
            try {
                const { data } = yield axios.post(`${this.dasHost}/${path}`, Object.assign({ clientId: this.dasClientId, clientSessionId: this.dasClientSessionId, authenticationSessionId: this.dasAuthenticationSessionId }, params), {
                    withCredentials: true,
                    headers: {
                        'correlation-id': this.dasClientSessionId,
                        'authorization': `Bearer ${this.authorization}`
                    }
                });
                return data;
            }
            catch (error) {
                // transform Axios Error into Event Error
                const axiosError = error;
                const httpResponseCode = (_b = axiosError.response) === null || _b === void 0 ? void 0 : _b.status;
                const errorData = (_c = axiosError.response) === null || _c === void 0 ? void 0 : _c.data;
                const dasError = errorData && transformDasError(errorData, httpResponseCode);
                const genericError = {
                    reason: 'XHR_REQUEST_FAILED',
                    httpResponseCode,
                    message: `Request failed with status ${httpResponseCode}`
                };
                this.kpi.queueError(dasError || genericError);
            }
        });
        /**
         * Returns real-time KPI metadata.
         */
        this.kpiMeta = () => {
            var _a;
            return ({
                authenticationData: {
                    multiFactorAuthenticatorData: Object.assign(Object.assign({}, getMfaMethodKpiMeta(this.authenticationMethod, this.authenticationContext)), { dasAuthenticationSessionId: this.dasAuthenticationSessionId, dasClientId: this.dasClientId })
                },
                cardData: {
                    cardVerificationData: {
                        cardAuthVerificationType: (_a = this
                            .authenticationMethod) === null || _a === void 0 ? void 0 : _a.authenticationMethodType
                    }
                }
            });
        };
        this.dasClientSessionId = payload.srcCorrelationId;
        this.dasClientId = payload.dasClientId;
        this.dasHost = payload.dasHost;
        this.kpi = new Kpi(payload, this.kpiMeta.bind(this));
    }
}
//# sourceMappingURL=index.js.map